import { Link, useNavigate, useParams } from 'react-router-dom'
import { Badge } from '@/components/ui/Badge'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Container, ContainerContent } from '@/components/ui/Container'
import { toast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { invalidateOcWorkflowData, workflowDetailQueryOptions } from '@/features/order-confirmation/workflowListQueries'
import { WORKFLOW_METRIC_COPY } from '@/features/order-confirmation/workflowMetricCopy'
import { markManualHandlingOrderConfirmation, ApiRequestError, getOrderConfirmationDetail } from '@/services/api'
import { AttachmentSection, EmailSourceSection, ManualSourceSection } from '../components/OcSource'

export function UnmatchedOrderConfirmationPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { workflowId, orderConfirmationId } = useParams<{
    workflowId: string
    orderConfirmationId: string
  }>()
  const detailQuery = useQuery({
    queryKey: ['order-confirmation-detail', workflowId, orderConfirmationId],
    queryFn: () => getOrderConfirmationDetail(orderConfirmationId!),
    enabled: !!workflowId && !!orderConfirmationId,
    ...workflowDetailQueryOptions,
  })

  const manualHandlingMutation = useMutation({
    mutationFn: () => markManualHandlingOrderConfirmation(orderConfirmationId!),
    onSuccess: () => {
      invalidateOcWorkflowData(queryClient, workflowId, { orderConfirmationId })
      toast({ title: 'Moved to manual handling' })
      navigate(`/workflow/${workflowId}/overview/todo`)
    },
    onError: (err: unknown) => toast({
      variant: 'destructive',
      title: 'Could not move to manual handling',
      description: err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : 'Something went wrong.',
    }),
  })

  const data = detailQuery.data
  const oc = data?.order_confirmation
  const linkedId = data?.linked_purchase_order_id
  const hasAttachment = !!oc?.source.attachment
  const hasEmail = !!oc?.source.email

  if (detailQuery.isLoading) {
    return <div className="flex justify-center py-16 text-sm text-gray-400">Loading…</div>
  }

  if (detailQuery.isError || !data || !oc) {
    return (
      <div className="max-w-lg space-y-4">
        <p className="text-sm text-gray-600">Could not load this order confirmation.</p>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to={`/workflow/${workflowId}/overview/todo`}>Back to overview</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="ghost" size="sm" className="-ml-2 gap-1 text-on-canvas-muted hover:bg-white/40 hover:text-on-canvas" asChild>
          <Link to={`/workflow/${workflowId}/overview/todo`}>
            <ArrowLeft className="h-4 w-4" />
            {WORKFLOW_METRIC_COPY.todo.label}
          </Link>
        </Button>
        <span className="text-sm text-on-canvas-muted">OC {oc.id}</span>
        <Badge tone="neutral">{oc.internal_status}</Badge>
      </div>

      {!linkedId && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <div className="flex min-w-0 items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p>
              This item could not be matched against a Purchase Order, and it needs to be handled manually.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 border-amber-300 bg-white hover:bg-amber-50"
            disabled={manualHandlingMutation.isPending}
            onClick={() => manualHandlingMutation.mutate()}
          >
            {manualHandlingMutation.isPending ? 'Moving…' : 'Move to Manual Handling'}
          </Button>
        </div>
      )}

      {linkedId && (
        <div className="rounded-lg border border-blue-100 bg-blue-50/80 px-4 py-3 text-sm text-blue-900">
          <p>
            This confirmation is already linked to purchase order{' '}
            <span className="font-medium">{data.linked_purchase_order_number ?? linkedId}</span>.
          </p>
          <Button type="button" variant="outline" size="sm" className="mt-3" asChild>
            <Link to={`/workflow/${workflowId}/purchase-order/${linkedId}`}>Open purchase order</Link>
          </Button>
        </div>
      )}

      <Container>
        <ContainerContent className="p-5 sm:p-6">
          <div
            className={cn(
              'grid gap-6',
              hasEmail && hasAttachment && 'lg:grid-cols-2',
              (hasEmail || hasAttachment) && !(hasEmail && hasAttachment) && 'max-w-3xl',
            )}
          >
            {hasEmail && (
              <EmailSourceSection
                label="Email"
                email={oc.source.email!}
                attachment={oc.source.attachment}
                resetKey={oc.id}
                prominent
              />
            )}
            {hasAttachment && (
              <AttachmentSection attachment={oc.source.attachment!} prominent />
            )}
            {!hasEmail && !hasAttachment && (
              <ManualSourceSection label="Source" oc={oc} />
            )}
          </div>
        </ContainerContent>
      </Container>
    </div>
  )
}
